// src/components/MobileSidebar.jsx
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import logoD from "../assets/ASitesPlanner2.png";
import logoL from "../assets/ASitesPlanner (1).png";
import { IoHome, IoSettingsSharp } from "react-icons/io5";
import { FaUser, FaBell, FaMoneyBillTrendUp, FaMoon } from "react-icons/fa6";
import { FaCheckCircle } from "react-icons/fa";
import { IoIosSunny } from "react-icons/io";
import { GiStairsGoal } from "react-icons/gi";
import { GoGoal } from "react-icons/go";
import { LuClockArrowUp } from "react-icons/lu";
import { RiSettings3Fill } from "react-icons/ri";
import { FaCalendarCheck } from "react-icons/fa6";
import { IoLanguageSharp } from "react-icons/io5";

// Import flags
import enFlag from "../assets/flags/engFlag.webp";
import alFlag from "../assets/flags/albanianFlag.png";
import frFlag from "../assets/flags/frFlag.webp";
import deFlag from "../assets/flags/grFlag.jpg";

// 1. IMPORT AGENDA POPUP
import AgendaPopup from "./AgendaPopup";

const MobileSidebar = ({ theme, switchTheme }) => {
  const { currentUser, isEmployee } = useAuth();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isAgendaOpen, setIsAgendaOpen] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef(null);

  // Define available languages with their data
  const languages = [
    {
      code: "en",
      name: "English",
      flag: enFlag,
      nativeName: "English",
    },
    {
      code: "al",
      name: "Albanian",
      flag: alFlag,
      nativeName: "Shqip",
    },
    {
      code: "fr",
      name: "French",
      flag: frFlag,
      nativeName: "Français",
    },
    {
      code: "de",
      name: "German",
      flag: deFlag,
      nativeName: "Deutsch",
    },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    setShowLanguageDropdown(false);

    // Update HTML direction for RTL languages
    if (languageCode === "ar") {
      document.documentElement.dir = "rtl";
    } else {
      document.documentElement.dir = "ltr";
    }
  };

  // Function to close sidebar AND open Agenda
  const handleOpenAgenda = () => {
    setOpen(false);
    setIsAgendaOpen(true);
  };

  // Function to close sidebar when clicking a link
  const handleLinkClick = () => {
    setOpen(false);
  };

  return (
    <>
      <AgendaPopup
        isOpen={isAgendaOpen}
        onClose={() => setIsAgendaOpen(false)}
      />

      {/* Hamburger button (always visible when mobile) */}
      <Hamburger onClick={() => setOpen(true)}>☰</Hamburger>

      {/* Overlay */}
      {open && <Backdrop onClick={() => setOpen(false)} />}

      {/* Sidebar Content */}
      <SidebarContainer open={open}>
        <CloseButton onClick={() => setOpen(false)}>×</CloseButton>

        {/* LOGO */}
        <img
          className="logo"
          src={theme === "dark" ? logoD : logoL}
          alt="Logo"
        />
        <div className="icons">
          {/* AGENDA BUTTON */}
          <FaCalendarCheck
            className="icon2"
            onClick={handleOpenAgenda}
            title="Open Today's Agenda"
          />

          {/* LANGUAGE SELECTOR */}
          <div className="language-selector" ref={languageDropdownRef}>
            <IoLanguageSharp
              className="icon2"
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              title={t("navbar.changeLanguage") || "Change Language"}
            />

            {showLanguageDropdown && (
              <div className="language-dropdown">
                {languages.map((language) => (
                  <div
                    key={language.code}
                    className={`language-option ${
                      i18n.language === language.code ? "active" : ""
                    }`}
                    onClick={() => handleLanguageChange(language.code)}
                  >
                    <img
                      src={language.flag}
                      alt={language.name}
                      className="language-flag"
                    />
                    <div className="language-info">
                      <span className="language-name">{language.name}</span>
                      <span className="language-native">
                        {language.nativeName}
                      </span>
                    </div>
                    {i18n.language === language.code && (
                      <span className="language-check">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* THEME TOGGLE BUTTON - Circular like other icons */}
          <ThemeToggleButton
            className="icon2"
            onClick={switchTheme}
            title={
              theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"
            }
          >
            {theme === "dark" ? <IoIosSunny /> : <FaMoon />}
          </ThemeToggleButton>

          <Link to={"/settings"} onClick={handleLinkClick}>
            <RiSettings3Fill className="icon2" />
          </Link>
        </div>

        <Content>
          {/* USER */}
          {currentUser && (
            <Link to="/account" onClick={handleLinkClick}>
              <div className="user link-styles2">
                <FaUser className="icon" />
                {currentUser.displayName}
              </div>
            </Link>
          )}

          {/* MENU LINKS */}
          <Menu>
            <StyledLink to="/app" onClick={handleLinkClick}>
              <IoHome className="icon" /> {t("sidebar.home")}
            </StyledLink>

            <StyledLink to="/growth" onClick={handleLinkClick}>
              <GiStairsGoal className="icon" /> {t("sidebar.myGrowth")}
            </StyledLink>

            <StyledLink to="/reminders" onClick={handleLinkClick}>
              <FaBell className="icon" /> {t("sidebar.reminders")}
            </StyledLink>

            <StyledLink to="/goals" onClick={handleLinkClick}>
              <GoGoal className="icon" /> {t("sidebar.goals")}
            </StyledLink>

            <StyledLink to="/work-hours" onClick={handleLinkClick}>
              <LuClockArrowUp className="icon" /> {t("sidebar.workHours")}
            </StyledLink>

            {isEmployee && (
              <StyledLink to="/work-tracker" onClick={handleLinkClick}>
                <FaCheckCircle className="icon" /> {t("sidebar.workTracker")}
              </StyledLink>
            )}

            <StyledLink to="/finances" onClick={handleLinkClick}>
              <FaMoneyBillTrendUp className="icon" /> {t("sidebar.finances")}
            </StyledLink>

            <StyledLink to="/settings" onClick={handleLinkClick}>
              <IoSettingsSharp className="icon" /> {t("sidebar.settings")}
            </StyledLink>
          </Menu>
        </Content>

        {/* THEME SWITCH (alternative style) */}
        <ToggleSwitch onClick={switchTheme} isDark={theme === "dark"}>
          <IoIosSunny className="icon sun" />
          <FaMoon className="icon moon" />
          <div className="thumb" />
        </ToggleSwitch>
      </SidebarContainer>
    </>
  );
};

export default MobileSidebar;

/* -------- STYLING -------- */

const Hamburger = styled.div`
  font-size: 22px;
  padding: 7px 15px;
  cursor: pointer;
  position: fixed;
  top: 10px;
  left: 15px;
  z-index: 200000;
  color: var(--fontPrimaryColor);
  border-radius: 10px;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.5);
  z-index: 199;
`;

const SidebarContainer = styled.div`
  width: 80%;
  height: 100vh;
  background: var(--secondaryBackgroundColor);
  position: fixed;
  top: 0;
  left: ${({ open }) => (open ? "0" : "-100%")};
  z-index: 2500000;
  transition: 0.35s ease;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  border-radius: 0 25px 25px 0;

  .icons {
    margin: 2em 0;
    display: flex;
    gap: 10px;
    justify-content: center;
    position: relative;

    .icon2 {
      padding: 17px;
      color: var(--fontPrimaryColor);
      border-radius: 50%;
      cursor: pointer;
      font-size: 3.5em;
      background-color: var(--thirdBackgroundColor);
      transition: 0.5s;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: var(--secondaryBackgroundColor);
      }
    }

    /* Language Selector Styles */
    .language-selector {
      position: relative;
      display: inline-block;
    }

    .language-dropdown {
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      min-width: 200px;
      background: var(--primaryBackgroundColor);
      border: 1px solid var(--borderColor);
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      z-index: 3000;
      overflow: hidden;
      margin-top: 10px;

      .language-option {
        display: flex;
        align-items: center;
        padding: 10px 15px;
        cursor: pointer;
        transition: background-color 0.2s;
        border-bottom: 1px solid var(--borderColor);

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background-color: var(--secondaryBackgroundColor);
        }

        &.active {
          background-color: var(--thirdBackgroundColor);
        }

        .language-flag {
          width: 24px;
          height: 16px;
          border-radius: 2px;
          margin-right: 12px;
          object-fit: cover;
        }

        .language-info {
          display: flex;
          flex-direction: column;
          flex-grow: 1;

          .language-name {
            font-size: 0.9em;
            font-weight: 500;
            color: var(--fontPrimaryColor);
          }

          .language-native {
            font-size: 0.8em;
            color: var(--fontSecondaryColor);
          }
        }

        .language-check {
          color: var(--primaryColor);
          font-weight: bold;
        }
      }
    }

    @media (max-height: 850px) {
      margin: 1em 0 10px 0;
      .icon2 {
        font-size: 3em;
      }
    }
  }

  .logo {
    width: 150px;
    align-self: center;
    margin-bottom: 20px;
  }
  @media (max-height: 850px) {
    .logo {
      display: none;
    }
  }
`;

const CloseButton = styled.div`
  position: absolute;
  right: 15px;
  top: 15px;
  font-size: 32px;
  cursor: pointer;
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
`;

const Content = styled.div`
  margin-top: 20px;

  .user {
    background: var(--thirdBackgroundColor);
    padding: 16px 18px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 25px;
    text-decoration: none;
    color: var(--fontPrimaryColor);

    .icon {
      font-size: 20px;
    }
    @media (max-height: 850px) {
      margin-bottom: 7px;
    }
  }
`;

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media (max-height: 850px) {
    gap: 1px;
  }
`;

const StyledLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 18px;
  color: var(--fontPrimaryColor);
  text-decoration: none;
  font-size: 17px;

  .icon {
    font-size: 22px;
  }

  &:hover {
    background: rgba(56, 189, 248, 0.07);
    color: #38bdf8;
  }

  &.active {
    color: #38bdf8;
  }
  @media (max-height: 850px) {
    font-size: 14px;
    .icon {
      font-size: 20px;
    }
  }
`;

const ThemeToggleButton = styled.button`
  padding: 1px;
  color: var(--fontPrimaryColor);
  border-radius: 50%;
  cursor: pointer;
  font-size: 25em;
  background-color: var(--thirdBackgroundColor);
  transition: 0.5s;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  &:hover {
    background-color: var(--secondaryBackgroundColor);
  }

  svg {
    width: 27px;
    height: 27px;
  }
`;

const ToggleSwitch = styled.div`
  position: absolute;
  bottom: 3em;
  left: 50%;
  transform: translateX(-50%);
  width: 62px;
  height: 30px;
  background: ${({ isDark }) => (isDark ? "#495269" : "#e8e8e8")};
  border-radius: 40px;
  padding: 4px;
  cursor: pointer;
  transition: background 0.3s ease;

  .icon {
    position: absolute;
    font-size: 22px;
    opacity: 0.45;
  }

  .sun {
    left: 5px;
  }

  .moon {
    right: 9px;
    font-size: 20px;
  }

  .thumb {
    width: 22px;
    height: 22px;
    background: ${({ isDark }) => (isDark ? "#fff" : "#111")};
    border-radius: 50%;
    position: absolute;
    transform: ${({ isDark }) =>
      isDark ? "translateX(32px)" : "translateX(0)"};
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
      background 0.3s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  }
  @media (max-height: 850px) {
    bottom: 10em;
  }
`;
